/**
 * Utility functions for standardized error handling
 */

import { ZodError } from "zod";

/**
 * Standardized response structure for all server actions
 */
export interface ActionResponse<T = unknown> {
  success: boolean;
  data?: T;
  error?: string;
  /** Server-side stack trace when the error originated on the server; included so Rollbar can show the real throw site. */
  errorStack?: string;
  message?: string;
  validationErrors?: Record<string, any>;
}

/**
 * Create a standardized error response for server actions
 */
export function createErrorResponse(
  error: unknown,
  defaultMessage = "An error occurred",
): ActionResponse<never> {
  // Handle Zod validation errors
  if (error instanceof ZodError) {
    return {
      error: "Validation error",
      success: false,
      validationErrors: error.format(),
    };
  }

  // Handle standard errors
  if (error instanceof Error) {
    return {
      error: error.message,
      success: false,
      ...(error.stack && { errorStack: error.stack }),
    };
  }

  // Handle unknown errors
  return { error: defaultMessage, success: false };
}

export function extractErrorMessage(
  error: unknown,
  defaultMessage: string,
): string {
  if (error instanceof Error) return error.message;
  if (typeof error === "string") return error;

  return defaultMessage;
}

const SES_ERROR_KEYWORDS = [
  "invalidclienttokenid",
  "security token",
  "credentials",
  "access denied",
  "signature",
  "ses",
];

function isCredentialError(lower: string): boolean {
  return SES_ERROR_KEYWORDS.some((keyword) => lower.includes(keyword));
}

/**
 * Map email/SES delivery errors to a user-facing message (no raw AWS or credential text).
 */
export function getUserFacingEmailError(
  error: unknown,
  defaultMessage: string,
): string {
  const msg = extractErrorMessage(error, defaultMessage);

  if (isCredentialError(msg.toLowerCase())) {
    return "Verification email could not be sent. Email delivery is not configured or credentials are invalid (check AWS SES).";
  }

  return defaultMessage;
}

/**
 * Create a standardized success response for server actions
 */
export function createSuccessResponse<T>(
  data: T,
  message?: string,
): ActionResponse<T> {
  return {
    data,
    message,
    success: true,
  };
}

/**
 * Type guard to check if a response is successful
 */
export function isSuccessResponse<T>(
  response: ActionResponse<T>,
): response is ActionResponse<T> & { success: true; data: T } {
  return response.success === true;
}

/**
 * Type guard to check if a response is an error
 */
export function isErrorResponse<T>(
  response: ActionResponse<T>,
): response is ActionResponse<T> & { success: false; error: string } {
  return response.success === false;
}

/** Message Next.js returns when the client invokes a Server Action ID that the server no longer has (e.g. after a deploy). */
const SERVER_ACTION_NOT_FOUND_MARKER = "was not found on the server";

/**
 * Returns true if the error is Next.js "Server Action ... was not found on the server"
 * (e.g. UnrecognizedActionError after a deployment while the user had a long-lived tab).
 */
export function isServerActionNotFoundError(error: unknown): boolean {
  const message =
    error instanceof Error
      ? error.message
      : typeof error === "string"
        ? error
        : "";

  return message.includes(SERVER_ACTION_NOT_FOUND_MARKER);
}

/** User-facing message when the server action not found error is detected. */
export const SERVER_ACTION_STALE_MESSAGE =
  "The page may have been updated. Please refresh the page and try again.";

/**
 * For use in catch blocks that call Server Actions. Returns a user-friendly message:
 * if the error is the stale Server Action ID case, returns SERVER_ACTION_STALE_MESSAGE;
 * otherwise returns fallback.
 */
export function getUserMessageForServerActionError(
  error: unknown,
  fallback: string,
): string {
  return isServerActionNotFoundError(error)
    ? SERVER_ACTION_STALE_MESSAGE
    : fallback;
}
